import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sparkles, ArrowLeft, ArrowRight } from 'lucide-react';
import { shareApi } from '../../services/shareApi';
import { SharedStudySession } from '../../types/share.types';
import StudyMaterialRenderer from '../StudyMaterialRenderer';
import { SpaceCard } from '../ui/Card';
import { Button } from '../ui/Button';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { MixedText } from '../../utils/rtl';
import { PageContainer } from '../layout/PageContainer';
import RestrictedChatButton from './RestrictedChatButton';
import RestrictedAccessModal from './RestrictedAccessModal';

const SharedStudyMaterialsView: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'he';

  const [session, setSession] = useState<SharedStudySession | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showRestrictedModal, setShowRestrictedModal] = useState(false);

  useEffect(() => {
    const fetchSharedSession = async () => {
      if (!token) {
        setError(t('common:errors.not_found', 'Shared session not found'));
        setLoading(false);
        return;
      }
      try {
        const data = await shareApi.getSharedStudySession(token);
        setSession(data);
      } catch (err) {
        setError(t('common:errors.loading_failed', 'Unable to load shared study materials.'));
      } finally {
        setLoading(false);
      }
    };

    fetchSharedSession();
  }, [token, t]);

  const handleContinue = () => {
    setShowRestrictedModal(true);
  };

  if (loading) {
    return (
      <PageContainer backgroundIntensity="minimal" showParticles={false}>
        <div className="flex items-center justify-center py-16">
          <LoadingSpinner />
        </div>
      </PageContainer>
    );
  }

  if (error || !session) {
    return (
      <PageContainer backgroundIntensity="minimal" showParticles={false}>
        <SpaceCard className="p-8 text-center">
          <p className="text-surface-primary mb-4 font-semibold">{error || t('common:errors.not_found', 'Shared session not found')}</p>
          <Button variant="primary" onClick={() => navigate('/login')}>
            {t('common:navigation.login', 'Log in')}
          </Button>
        </SpaceCard>
      </PageContainer>
    );
  }

  return (
    <PageContainer backgroundIntensity="minimal" showParticles={false}>
      <div className="space-y-6">
        <div className="flex flex-col gap-3">
          <h1 className={`text-3xl font-bold text-bg-secondary leading-tight font-hebrew ${isRTL ? 'text-right' : ''}`}>
            <MixedText>{session.title}</MixedText>
          </h1>
          {session.description && (
            <p className={`text-surface-secondary max-w-2xl ${isRTL ? 'text-right' : ''}`}>
              <MixedText>{session.description}</MixedText>
            </p>
          )}
        </div>

        {session.ai_generated_description && (
          <SpaceCard className={`p-6 border border-vibrant-orange/20 bg-gradient-to-br from-white to-vibrant-orange/5 ${isRTL ? 'text-right' : ''}`}>
             <div className={`flex items-center mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={`w-8 h-8 bg-vibrant-orange/10 rounded-lg flex items-center justify-center ${isRTL ? 'ml-3' : 'mr-3'}`}>
                   <Sparkles className="w-4 h-4 text-vibrant-orange" />
                </div>
                <h3 className="text-lg font-semibold text-surface-primary">
                   <MixedText>{t('labels.ai_summary', 'AI Summary')}</MixedText>
                </h3>
             </div>
             <p className="text-sm text-surface-secondary leading-relaxed">
               <MixedText>{session.ai_generated_description}</MixedText>
             </p>
          </SpaceCard>
        )}

        {session.materials.length > 0 ? (
          <div className="space-y-6">
            <SpaceCard className="p-6 border border-vibrant-orange/20 bg-gradient-to-br from-white to-vibrant-orange/5">
              <StudyMaterialRenderer
                content={session.materials[0].content}
                materialType={session.materials[0].material_type}
                isRTL={isRTL}
              />
            </SpaceCard>
            
            <div className="flex justify-center">
              <Button
                onClick={handleContinue}
                variant="primary"
                icon={isRTL ? <ArrowLeft className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
                iconPosition={isRTL ? "left" : "right"}
                className="w-full sm:w-auto min-w-[200px] justify-center"
              >
                {t('sessions:flow.materials.continueToPractice', 'Continue to Practice')}
              </Button>
            </div>
          </div>
        ) : (
          <SpaceCard className="p-8 text-center">
            <p className="text-surface-secondary">
              <MixedText>{t('sessions:share.empty', 'No detailed summary is available yet.')}</MixedText>
            </p>
          </SpaceCard>
        )}

        <SpaceCard className={`p-8 text-center border border-vibrant-orange/30 bg-gradient-to-br from-muted-purple/20 to-vibrant-orange/10 ${isRTL ? 'rtl' : ''}`}>
           <h3 className="text-xl font-bold text-surface-primary mb-4">
             <MixedText>{t('common:labels.share_cta_title', 'Want your own? Sign up')}</MixedText>
           </h3>
           <div className="flex justify-center">
             <Button variant="primary" onClick={() => navigate('/register')} className="px-8">
               <MixedText>{t('common:labels.share_cta_button', 'Sign Up Now')}</MixedText>
             </Button>
           </div>
        </SpaceCard>
      </div>
      <RestrictedChatButton />
      <RestrictedAccessModal 
        isOpen={showRestrictedModal} 
        onClose={() => setShowRestrictedModal(false)} 
      />
    </PageContainer>
  );
};

export default SharedStudyMaterialsView;
